import { API_CONFIG, setApiBaseUrl, setGraphqlBaseUrl } from "../config/api";
import {
	formatValidationErrors,
	isValidationError,
	parseValidationErrors,
	type ValidationError,
} from "./utils/validationErrors";

export interface ApiClientOptions {
	instanceId?: string;
	getAccessToken?: (forceRefresh?: boolean) => Promise<string | null>;
	baseUrl?: string;
	graphqlBaseUrl?: string;
}

let globalOptions: ApiClientOptions = {};

export function configureApiClient(options: ApiClientOptions): void {
	globalOptions = options;
	if (options.baseUrl) {
		setApiBaseUrl(options.baseUrl);
	}
	if (options.graphqlBaseUrl) {
		setGraphqlBaseUrl(options.graphqlBaseUrl);
	}
}

export function getApiClientOptions(): ApiClientOptions {
	return globalOptions;
}

export interface RequestOptions {
	method?: "GET" | "POST" | "PUT" | "PATCH" | "DELETE";
	body?: unknown;
	headers?: Record<string, string>;
	isMultipart?: boolean;
}

export class ApiError extends Error {
	public validationErrors?: ValidationError[];

	constructor(
		message: string,
		public status: number,
		public statusText: string,
		public code?: string,
		validationErrors?: ValidationError[],
	) {
		super(message);
		this.name = "ApiError";
		this.validationErrors = validationErrors;
	}
}

async function getHeaders(
	options: RequestOptions = {},
): Promise<Record<string, string>> {
	const headers: Record<string, string> = {
		...options.headers,
	};

	if (!options.isMultipart) {
		headers["Content-Type"] = "application/json";
	}

	if (globalOptions.instanceId) {
		headers["x-instance-id"] = globalOptions.instanceId;
	}

	if (globalOptions.getAccessToken) {
		try {
			const token = await globalOptions.getAccessToken();
			if (token) {
				headers["Authorization"] = `Bearer ${token}`;
			}
		} catch (error) {
			console.warn("Failed to get access token:", error);
		}
	}

	return headers;
}

export async function apiRequest<T>(
	endpoint: string,
	options: RequestOptions = {},
): Promise<T> {
	const { method = "GET", body, isMultipart } = options;
	const url = `${API_CONFIG.baseUrl}/api/v1/${endpoint}`;
	const headers = await getHeaders(options);

	const fetchOptions: RequestInit = {
		method,
		headers,
	};

	if (body !== undefined) {
		if (isMultipart && body instanceof FormData) {
			fetchOptions.body = body;
		} else {
			fetchOptions.body = JSON.stringify(body);
		}
	}

	const response = await fetch(url, fetchOptions);

	if (!response.ok) {
		let errorMessage = response.statusText;
		let errorCode: string | undefined;
		let validationErrors: ValidationError[] | undefined;

		try {
			const errorBody = (await response.json()) as {
				message?: string;
				Message?: string;
				code?: string;
			};
			if (errorBody?.Message) {
				errorMessage = errorBody.Message;
			} else if (errorBody?.message) {
				errorMessage = errorBody.message;
			}
			errorCode = errorBody?.code;

			// Parse validation errors if this is a validation failure
			if (isValidationError(errorCode)) {
				const parsedErrors = parseValidationErrors(errorMessage);
				if (parsedErrors) {
					validationErrors = parsedErrors;
					errorMessage = formatValidationErrors(parsedErrors);
				}
			}
		} catch {
			// If parsing fails, use the default message
		}

		throw new ApiError(
			errorMessage,
			response.status,
			response.statusText,
			errorCode,
			validationErrors,
		);
	}

	if (response.status === 204) {
		return undefined as T;
	}

	const contentLength = response.headers.get("content-length");
	if (contentLength === "0") {
		return undefined as T;
	}

	return response.json();
}

export async function apiGet<T>(endpoint: string): Promise<T> {
	return apiRequest<T>(endpoint, { method: "GET" });
}

export async function apiPost<T>(endpoint: string, body?: unknown): Promise<T> {
	return apiRequest<T>(endpoint, { method: "POST", body });
}

export async function apiPut<T>(endpoint: string, body?: unknown): Promise<T> {
	return apiRequest<T>(endpoint, { method: "PUT", body });
}

export async function apiPatch<T>(
	endpoint: string,
	body?: unknown,
): Promise<T> {
	return apiRequest<T>(endpoint, { method: "PATCH", body });
}

export async function apiDelete<T>(endpoint: string): Promise<T> {
	return apiRequest<T>(endpoint, { method: "DELETE" });
}

export async function apiDownloadBlob(endpoint: string): Promise<Blob> {
	const url = `${API_CONFIG.baseUrl}/api/v1/${endpoint}`;
	const headers = await getHeaders();

	const response = await fetch(url, { method: "GET", headers });

	if (!response.ok) {
		let errorMessage = response.statusText;
		try {
			const errorBody = (await response.json()) as {
				message?: string;
				Message?: string;
			};
			errorMessage = errorBody?.Message ?? errorBody?.message ?? errorMessage;
		} catch {
			// If parsing fails, use the default message
		}
		throw new ApiError(errorMessage, response.status, response.statusText);
	}

	return response.blob();
}

export async function apiUpload<T>(
	endpoint: string,
	formData: FormData,
): Promise<T> {
	return apiRequest<T>(endpoint, {
		method: "POST",
		body: formData,
		isMultipart: true,
	});
}

// Upload metadata types
export interface FileInfoResponse {
  fileName: string;
  fileExtension: string;
  mimeType: string;
  fileSizeBytes: number;
}

export interface UploadFileResponse {
  uploadedFileId: string;
  fileInfo: FileInfoResponse;
  uploadedAt: string;
  expiresAt: string | null;
  uploaderUserId: string;
  isValid: boolean;
  errorMessage: string | null;
  metadata: Record<string, string> | null;
  tags: string[] | null;
}

export interface ValidateExcelDataSourceRequest {
  uploadedFileId: string;
  transactionType: string;
  columnMappings: { forecasterId: string; columnLetter: string; forecasterLabel: string }[];
  formatDeclaration: { negativeFormat: string; dateFormat: string; dateFormatOther?: string };
  splitConfigs?: { sourceColumn: string; negativeIs: string }[];
}

export interface RowValidationError {
  rowNumber: number;
  columnLetter: string;
  fieldName: string;
  expected: string;
  found: string;
}

export interface ValidateExcelDataSourceResponse {
  isValid: boolean;
  totalRows: number;
  validRows: number;
  invalidRows: number;
  errors: RowValidationError[];
}

export async function getUploadedFileMetadata(uploadedFileId: string): Promise<UploadFileResponse> {
  return apiGet<UploadFileResponse>(`upload/file/${uploadedFileId}/metadata`);
}

export async function validateExcelDataSource(
  request: ValidateExcelDataSourceRequest
): Promise<ValidateExcelDataSourceResponse> {
  return apiPost<ValidateExcelDataSourceResponse>('data-sources/excel/validate', request);
}

export interface ColumnSchemaField {
  forecasterId: string;
  forecasterLabel: string;
  description: string;
  required: boolean;
  fieldType: 'Text' | 'Date' | 'Currency' | 'Number';
}

export interface ColumnMappingPreviewResponse {
  schema: ColumnSchemaField[];
  autoMappings: Record<string, string | null> | null;
  headers: string[] | null;
  sampleRows: string[][] | null;
}

export async function getColumnMappingPreview(
  uploadedFileId: string,
  transactionType: string
): Promise<ColumnMappingPreviewResponse> {
  return apiPost<ColumnMappingPreviewResponse>('data-sources/excel/column-mapping-preview', {
    uploadedFileId,
    transactionType,
  });
}
